import { RefinancingHero } from '@/components/RefinancingHero/index.ts';
import { Button } from '@/ui/button-2.tsx';

import Page from '../../components/Page/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import useBrainSession from '../../services/BrainSession/useBrainSession.ts';
import { LANG } from '../../services/Config.js';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_MICROLOAN } from './RRS.ts';

const rowClassName = 'mx-auto flex w-full max-w-[900px]! flex-col flex-wrap sm:flex-row';

const debtCheckButtonClassName = 'max-w-[550px] self-center sm:min-w-[330px]';

type MicroloanProps = Record<string, never>;

const Microloan: React.FC<MicroloanProps> = () => {
  const { appStrings } = LANG;
  const { toggleChat } = useBrainSession();

  const handleDebtCheckClick = () => {
    toggleChat({
      goal: 'debt_check',
      step: 'refin_present_terms_auto_new',
    });
  };

  return (
    <Page title={appStrings.MENU.MICROLOAN}>
      <RrsHandler {...RRS_MICROLOAN} />
      <RefinancingHero chatStep="refin_present_terms_auto_new" />
      <Section>
        <div className="flex max-w-[900px]! flex-col">
          <h1 className="mb-6 typography-v1-legacy-h2 text-[#18A0FB] sm:mb-8">
            Slik fungerer mikrolån
          </h1>
          <p className="typography-v1-legacy-subtitle2">
            Mikrolån er en dårlig idé fordi det ofte koster mer i renter og gebyrer enn forbrukslån
            og kredittkort.
          </p>
          <br />
          <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Hva er et mikrolån?</h3>
          <p className="typography-v1-legacy-subtitle2">
            Mikrolån er et lån på mellom 1.000 og 10.000 kroner, som vanligvis ikke stilles noen
            sikkerhet for.
          </p>
        </div>
      </Section>
      <Section background="paper" last>
        <div className={rowClassName}>
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Lovligheten av mikrolån</h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Tidligere var det lov å ta opp mikrolån kun ved å sende en SMS til en lånetilbyder.
              Denne praksisen ble imidlertid ulovlig for noen få år siden da det var svært
              forbrukerfiendtlig, og Finanstilsynet mente praksisen var uforsvarlig.
              <br />
              <br />
              Selv om det ikke lengre er lov å søke om mikrolån via SMS, er mikrolån som konsept
              fremdeles ikke ulovlig i Norge. Det er selvfølgelig lov å søke om lån på mindre
              summer, men i dag krever det ordinær søknadsbehandling av banken.
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Det er dyrt med mikrolån</h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Mikrolån er dyrere enn andre lånetyper fordi risikoen for utløp er høyere. Låntaker må
              betale renter og gebyrer over kortere tid, noe som resulterer i høyere totalkostnader.
              <br />
              <br />
              Mikrolån er et svært kostbart produkt fordi bankene låner ut penger uten sikkerhet. I
              tillegg er utlån summene små, og dermed blir administrasjonskostnaden forholdsmessig
              høy.
            </p>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Høye gebyrer</h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Det er ikke unormalt at bankene tar høye gebyrer og rentenivået, ofte ligger de på
              over 20 prosent, noe som gjør at de blir svært dyrt å benytte seg av.
              <br />
              <br />
              Når man tar et mikrolån, låner man en mindre sum penger som vanligvis er ment for
              forbruk. Ettersom renter og gebyrer ofte trekkes fra det opprinnelige beløpet, kan det
              være dyrt å ta opp små lån. Har du flere smålån, eller mikrolån vil det være
              fordelaktig å samle alle disse til et større forbrukslån.
              <br />
              <br />
              På denne måten vil du kunne årlig kunne spare betydelige beløp i rentekostnader og
              gebyrer. I tillegg vil det også være mer oversiktlig å forholde seg til èn bank, enn
              til flere.
              <br />
              <br />
              Ved refinansiering av alle disse lånene til et stort forbrukslån, vil man spare mye
              penger. Overskuddet av refinansiering vil da kunne brukes til å betale ned på lånet
              eller føre det over i en sparekonto.
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center">
          <Button
            color="yellow"
            className={debtCheckButtonClassName}
            onClick={handleDebtCheckClick}
          >
            Sjekk nå
          </Button>
          <Disclaimer />
        </div>
      </Section>
    </Page>
  );
};

export default Microloan;
